import { Component, Output, EventEmitter, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { LaboratoryService } from '@core/services/laboratory.service';
import { UserPhotoService } from '@core/services/user-photo.service';
import { ThemeService } from '@core/services/theme.service';
import { LoggerService } from '@core/services/logger.service';
import { UserAccessService } from '@core/services/user-access.service';
import { MessagesService } from '@features/messages/services/messages.service';
import { MessagesRealtimeService } from '@features/messages/services/messages-realtime.service';
import { User } from '@core/models/user.model';
import { Observable, Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Output() toggleSidebar = new EventEmitter<void>();

  currentUser$: Observable<User | null>;
  laboratoryRazaoSocial$: Observable<string | null>;
  userPhotoUrl: string | null = null;
  userInitials: string = '';
  truncatedName: string = '';
  dropdownOpen = false;
  showBell = false;
  unreadCount = 0;
  notificationDropdownOpen = false;
  private userSubscription?: Subscription;
  private notificationSubscriptions = new Subscription();

  constructor(
    public authService: AuthService,
    private laboratoryService: LaboratoryService,
    private userPhotoService: UserPhotoService,
    public themeService: ThemeService,
    private logger: LoggerService,
    private userAccess: UserAccessService,
    private messagesService: MessagesService,
    private messagesRealtime: MessagesRealtimeService,
    private router: Router
  ) {
    this.currentUser$ = this.authService.currentUser$;
    this.laboratoryRazaoSocial$ = this.laboratoryService.razaoSocial$;
  }

  ngOnInit(): void {
    this.userSubscription = this.currentUser$.subscribe(user => {
      if (user) {
        this.truncatedName = this.userPhotoService.truncateName(user.name, 15);
        this.userInitials = this.userPhotoService.getInitials(user.name);

        // Limpar foto anterior quando mudar de usuário
        if (this.userPhotoUrl) {
          URL.revokeObjectURL(this.userPhotoUrl);
          this.userPhotoUrl = null;
        }

        // Buscar foto do usuário (com tratamento de erro robusto)
        try {
          this.userPhotoService.getCurrentUserPhotoUrl().subscribe({
            next: (photoUrl) => {
              try {
                this.userPhotoUrl = photoUrl;
                this.logger.log('User photo loaded:', photoUrl ? 'yes' : 'no');
              } catch (error) {
                this.logger.error('Error processing user photo:', error);
                this.userPhotoUrl = null;
              }
            },
            error: (error) => {
              this.logger.error('Error fetching user photo:', error);
              this.userPhotoUrl = null;
            }
          });
        } catch (error) {
          this.logger.error('Critical error starting photo fetch:', error);
          this.userPhotoUrl = null;
        }
      } else {
        if (this.userPhotoUrl) {
          URL.revokeObjectURL(this.userPhotoUrl);
        }
        this.userPhotoUrl = null;
        this.userInitials = '';
        this.truncatedName = '';
      }
    });

    this.initNotificationBell();
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    this.notificationSubscriptions.unsubscribe();
    this.messagesRealtime.disconnect();
  }

  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  onLogout(): void {
    this.authService.logout();
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.dropdownOpen = !this.dropdownOpen;
  }

  closeDropdown(): void {
    this.dropdownOpen = false;
  }

  onToggleTheme(): void {
    this.themeService.toggle();
  }

  private initNotificationBell(): void {
    this.notificationSubscriptions.add(
      this.userAccess
        .check('NOTIFICACOES_COMUNICACAO_TIMES')
        .pipe(catchError(() => of(false)))
        .subscribe(canSee => {
          this.showBell = canSee;
          if (canSee) {
            this.loadUnreadCount();
            this.messagesRealtime.connect();
            this.notificationSubscriptions.add(
              this.messagesRealtime.unreadCountChanged$.subscribe(() => this.loadUnreadCount())
            );
          }
        })
    );
  }

  // Fetches the unread count once, then again every time MessagesRealtimeService pushes an
  // update (SSE — replaces the old 60s poll, US 59876). catchError degrades a failed fetch to
  // 0 without breaking the subscription to future pushes.
  private loadUnreadCount(): void {
    this.notificationSubscriptions.add(
      this.messagesService
        .getUnreadCount()
        .pipe(catchError(() => of({ inbox: 0, starred: 0, archived: 0 })))
        .subscribe(counts => {
          // Badge = received + archived; starred is intentionally excluded (US 59875).
          this.unreadCount = counts.inbox + counts.archived;
        })
    );
  }

  onNotificationClick(event: Event): void {
    event.stopPropagation();
    this.notificationDropdownOpen = !this.notificationDropdownOpen;
  }

  onNotificationCategoryClick(): void {
    this.notificationDropdownOpen = false;
    this.router.navigate(['/messages']);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.user-profile-dropdown')) {
      this.dropdownOpen = false;
    }
    if (!target.closest('.notification-dropdown')) {
      this.notificationDropdownOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.dropdownOpen = false;
    this.notificationDropdownOpen = false;
  }
}
